import { FAR_FUTURE, getMonthsElapsed, startOfMonthUTC, toDate } from './dates.js';
import {
  calculateEquilibriumCustomers,
  getProjectionKey,
  simulateProjectionsToMonth,
} from './customer-projection-capacity.js';
import type {
  AcquisitionCostModelInput,
  CustomerProjectionDataInput,
  CustomerProjectionInput,
  MonthlyCustomerMetrics,
  StandardizedCustomerData,
  SubscriptionTierInput,
} from './types.js';

export {
  calculateEquilibriumCustomers,
  getProjectionKey,
  simulateProjectionsToMonth,
} from './customer-projection-capacity.js';

function getMonthlySubscriptionPrice(tier: SubscriptionTierInput): number {
  switch (tier.billingFrequency) {
    case 'quarterly':
      return tier.price / 3;
    case 'yearly':
      return tier.price / 12;
    case 'monthly':
    default:
      return tier.price;
  }
}

function getVisitSeasonalityMultiplier(
  tier: SubscriptionTierInput,
  targetDate: Date,
): number {
  const month = targetDate.getUTCMonth() + 1;
  const seasonality = tier.visitSeasonality;
  if (!seasonality) return 1;
  return seasonality[month] ?? 1;
}

function calculateCustomerRevenue(
  tier: SubscriptionTierInput,
  customers: number,
  targetDate: Date,
): number {
  if (customers <= 0) return 0;

  const model = tier.revenueModel ?? 'subscription';
  const subscriptionComponent = getMonthlySubscriptionPrice(tier);
  const visitsPerCustomer =
    (tier.avgVisitsPerCustomerPerMonth ?? 0) *
    getVisitSeasonalityMultiplier(tier, targetDate);
  const visitComponent = (tier.pricePerVisit ?? 0) * visitsPerCustomer;

  switch (model) {
    case 'per_visit':
      return customers * visitComponent;
    case 'hybrid':
      return customers * (subscriptionComponent + visitComponent);
    case 'subscription':
    default:
      return customers * subscriptionComponent;
  }
}

function findAcquisitionCostModel(
  projection: CustomerProjectionInput,
  models: AcquisitionCostModelInput[],
): AcquisitionCostModelInput | undefined {
  if (projection.acquisitionCostModelId) {
    const byId = models.find((m) => m.id === projection.acquisitionCostModelId);
    if (byId) return byId;
  }
  if (projection.acquisitionChannel) {
    return models.find((m) => m.acquisitionChannel === projection.acquisitionChannel);
  }
  return undefined;
}

function resolveCostPerCustomer(
  projection: CustomerProjectionInput,
  model?: AcquisitionCostModelInput,
): number {
  return model?.fixedCostPerCustomer ?? projection.acquisitionCostPerCustomer ?? 0;
}

function buildProjectionWarnings(
  projection: CustomerProjectionInput,
  state: {
    customers: number;
    constrained: boolean;
  },
): string[] {
  const warnings: string[] = [];
  const equilibrium = calculateEquilibriumCustomers(projection);

  if (state.constrained) {
    warnings.push('Growth constrained by capacity limits this month.');
  }

  if (
    projection.maxCustomers &&
    projection.maxCustomers > 0 &&
    state.customers >= projection.maxCustomers * 0.95
  ) {
    warnings.push(
      `Near customer cap (${state.customers.toLocaleString()} / ${projection.maxCustomers.toLocaleString()}).`,
    );
  }

  if (equilibrium !== null && projection.maxCustomers) {
    if (equilibrium > projection.maxCustomers) {
      warnings.push(
        `Uncapped equilibrium (~${Math.round(equilibrium).toLocaleString()} customers) exceeds your cap.`,
      );
    }
  }

  return warnings;
}

export function simulateCustomerCount(
  projection: CustomerProjectionInput,
  monthsElapsed: number,
  options?: Parameters<typeof simulateProjectionsToMonth>[2],
): number {
  const states = simulateProjectionsToMonth([projection], monthsElapsed, options);
  return states.get(getProjectionKey(projection, 0))?.customers ?? projection.initialCustomers ?? 0;
}

function calculateCustomersAtMonth(
  projection: CustomerProjectionInput,
  monthsElapsed: number,
  options?: Parameters<typeof simulateProjectionsToMonth>[2],
): number {
  return simulateCustomerCount(projection, monthsElapsed, options);
}

function calculateProjectionMetrics(
  projection: CustomerProjectionInput,
  projectionIndex: number,
  targetDate: Date,
  subscriptionTiers: SubscriptionTierInput[],
  acquisitionCostModels: AcquisitionCostModelInput[],
  simulationOptions: Parameters<typeof simulateProjectionsToMonth>[2],
  allProjections: CustomerProjectionInput[],
): MonthlyCustomerMetrics {
  const empty: MonthlyCustomerMetrics = {
    totalCustomers: 0,
    newCustomers: 0,
    customersAcquired: 0,
    churnedCustomers: 0,
    subscriptionRevenue: 0,
    acquisitionCosts: 0,
    servicingCosts: 0,
    netCustomerChange: 0,
    customersByTier: {},
    averageRevenuePerCustomer: 0,
  };

  const projectionStartDate = toDate(projection.startDate);
  if (!projectionStartDate) return empty;

  const projectionEndDate = projection.endDate ? toDate(projection.endDate) : FAR_FUTURE;
  const targetMonth = startOfMonthUTC(targetDate);
  if (
    targetMonth.getTime() < startOfMonthUTC(projectionStartDate).getTime() ||
    (projectionEndDate && targetMonth.getTime() > startOfMonthUTC(projectionEndDate).getTime())
  ) {
    return empty;
  }

  const revenuePlan = subscriptionTiers.find((t) => t.id === projection.subscriptionTierId);
  if (!revenuePlan) return empty;

  const monthsElapsed = getMonthsElapsed(projectionStartDate, targetDate);
  const states = simulateProjectionsToMonth(allProjections, monthsElapsed, simulationOptions);
  const key = getProjectionKey(projection, projectionIndex);
  const currentState = states.get(key);
  const currentCustomers = currentState?.customers ?? projection.initialCustomers ?? 0;

  const previousMonthCustomers =
    monthsElapsed > 0
      ? calculateCustomersAtMonth(projection, monthsElapsed - 1, simulationOptions)
      : projection.initialCustomers ?? 0;

  const netNewCustomers = Math.max(0, currentCustomers - previousMonthCustomers);
  const churnRate = projection.churnRateMonthly ?? 0;
  const churnedCustomers = previousMonthCustomers * churnRate;
  const customersAcquired = Math.max(0, currentCustomers - previousMonthCustomers + churnedCustomers);

  const subscriptionRevenue = calculateCustomerRevenue(
    revenuePlan,
    currentCustomers,
    targetDate,
  );

  const acquisitionCostModel = findAcquisitionCostModel(projection, acquisitionCostModels);
  const costPerCustomer = resolveCostPerCustomer(projection, acquisitionCostModel);
  const acquisitionCosts = customersAcquired * costPerCustomer;
  const servicingCosts =
    currentCustomers * (projection.monthlyServicingCostPerCustomer ?? 0);

  const warnings = currentState
    ? buildProjectionWarnings(projection, currentState)
    : [];

  const capacityUtilization =
    projection.maxCustomers && projection.maxCustomers > 0
      ? currentCustomers / projection.maxCustomers
      : undefined;

  return {
    totalCustomers: currentCustomers,
    newCustomers: netNewCustomers,
    customersAcquired,
    churnedCustomers,
    subscriptionRevenue,
    acquisitionCosts,
    servicingCosts,
    netCustomerChange: currentCustomers - previousMonthCustomers,
    customersByTier: { [revenuePlan.id]: currentCustomers },
    averageRevenuePerCustomer:
      currentCustomers > 0 ? subscriptionRevenue / currentCustomers : 0,
    churnRateMonthly: churnRate,
    isCapacityConstrained: currentState?.constrained ?? false,
    capacityUtilization,
    warnings,
  };
}

export function getProjectedCustomerCountAtDate(
  projection: CustomerProjectionInput,
  targetDate: Date,
  options?: Parameters<typeof simulateProjectionsToMonth>[2],
): number {
  const projectionStartDate = toDate(projection.startDate);
  if (!projectionStartDate) {
    return projection.initialCustomers ?? 0;
  }

  const projectionEndDate = projection.endDate ? toDate(projection.endDate) : FAR_FUTURE;
  const targetMonth = startOfMonthUTC(targetDate);
  if (
    targetMonth.getTime() < startOfMonthUTC(projectionStartDate).getTime() ||
    (projectionEndDate &&
      targetMonth.getTime() > startOfMonthUTC(projectionEndDate).getTime())
  ) {
    return 0;
  }

  const monthsElapsed = getMonthsElapsed(projectionStartDate, targetDate);
  return calculateCustomersAtMonth(projection, monthsElapsed, options);
}

export function calculateMonthlyCustomerMetrics(
  targetDate: Date,
  data: CustomerProjectionDataInput | CustomerProjectionInput[],
  subscriptionTiers?: SubscriptionTierInput[],
  acquisitionCostModels: AcquisitionCostModelInput[] = [],
): MonthlyCustomerMetrics {
  const projections = Array.isArray(data) ? data : data.projections;
  const tiers = Array.isArray(data) ? subscriptionTiers ?? [] : data.subscriptionTiers;
  const models = Array.isArray(data)
    ? acquisitionCostModels
    : data.acquisitionCostModels ?? [];

  const simulationOptions = Array.isArray(data)
    ? {}
    : {
        capacityGroups: data.capacityGroups,
        capacityEvents: data.capacityEvents,
        growthScenarios: data.growthScenarios,
      };

  const result: MonthlyCustomerMetrics = {
    totalCustomers: 0,
    newCustomers: 0,
    customersAcquired: 0,
    churnedCustomers: 0,
    subscriptionRevenue: 0,
    acquisitionCosts: 0,
    servicingCosts: 0,
    netCustomerChange: 0,
    customersByTier: {},
    averageRevenuePerCustomer: 0,
    warnings: [],
  };

  let churnWeightedSum = 0;
  let churnWeight = 0;
  let constrainedCount = 0;

  projections.forEach((projection, index) => {
    const metrics = calculateProjectionMetrics(
      projection,
      index,
      targetDate,
      tiers,
      models,
      simulationOptions,
      projections,
    );
    result.totalCustomers += metrics.totalCustomers;
    result.newCustomers += metrics.newCustomers;
    result.customersAcquired += metrics.customersAcquired;
    result.churnedCustomers += metrics.churnedCustomers;
    result.subscriptionRevenue += metrics.subscriptionRevenue;
    result.acquisitionCosts += metrics.acquisitionCosts;
    result.servicingCosts += metrics.servicingCosts;
    result.netCustomerChange += metrics.netCustomerChange;

    for (const [tierId, count] of Object.entries(metrics.customersByTier)) {
      result.customersByTier[tierId] = (result.customersByTier[tierId] ?? 0) + count;
    }

    if (metrics.churnRateMonthly !== undefined && metrics.totalCustomers > 0) {
      churnWeightedSum += metrics.churnRateMonthly * metrics.totalCustomers;
      churnWeight += metrics.totalCustomers;
    }

    if (metrics.isCapacityConstrained) constrainedCount += 1;
    if (metrics.warnings?.length) {
      result.warnings = [...(result.warnings ?? []), ...metrics.warnings];
    }
  });

  result.averageRevenuePerCustomer =
    result.totalCustomers > 0 ? result.subscriptionRevenue / result.totalCustomers : 0;
  result.churnRateMonthly = churnWeight > 0 ? churnWeightedSum / churnWeight : undefined;
  result.isCapacityConstrained = constrainedCount > 0;
  result.capacityUtilization =
    result.totalCustomers > 0 && projections.length === 1 && projections[0]?.maxCustomers
      ? result.totalCustomers / projections[0].maxCustomers
      : undefined;

  return result;
}

export function toStandardizedCustomerData(
  metrics: MonthlyCustomerMetrics,
): StandardizedCustomerData {
  return {
    totalCustomers: metrics.totalCustomers,
    newCustomers: metrics.newCustomers,
    customersAcquired: metrics.customersAcquired,
    churnedCustomers: metrics.churnedCustomers,
    subscriptionRevenue: metrics.subscriptionRevenue,
    acquisitionCosts: metrics.acquisitionCosts,
    servicingCosts: metrics.servicingCosts,
    averageRevenuePerCustomer: metrics.averageRevenuePerCustomer,
    churnRateMonthly: metrics.churnRateMonthly,
    isCapacityConstrained: metrics.isCapacityConstrained,
    capacityUtilization: metrics.capacityUtilization,
    warnings: metrics.warnings,
  };
}

export function calculateLTV(
  customerData: StandardizedCustomerData | MonthlyCustomerMetrics,
  options?: { defaultChurnRateMonthly?: number; maxLifetimeMonths?: number },
): number {
  const arpu =
    'averageRevenuePerCustomer' in customerData
      ? customerData.averageRevenuePerCustomer
      : 0;
  const churn =
    'churnRateMonthly' in customerData && customerData.churnRateMonthly !== undefined
      ? customerData.churnRateMonthly
      : options?.defaultChurnRateMonthly ?? 0.05;
  const maxMonths = options?.maxLifetimeMonths ?? 120;

  if (churn <= 0) return arpu * maxMonths;
  return arpu / churn;
}
